'use client';

import { memo, useMemo } from 'react';
import { AgentsSection as CoreAgentsSection, FlipAgentCard, extractAgentsFromListings, type Agent } from '../../../../src/components/agents';
import { useTenantOptional } from '../../../../src/contexts/TenantContext';

interface ListingAgentSource {
  listingAgent: {
    id: string;
    name: string;
    email: string;
    avatar: string | null;
    jobTitle: string | null;
  } | null;
}

export interface RouteRoyalAgentsSectionProps {
  agents?: Agent[];
  listings?: ListingAgentSource[];
  title?: string;
  subtitle?: string;
  columns?: 1 | 2 | 3 | 4;
  messages?: Record<string, unknown>;
  locale?: 'en' | 'es' | 'fr';
}

const safeGetString = (obj: unknown, defaultValue: string): string => {
  if (typeof obj === 'string') return obj;
  return defaultValue;
};

export const RouteRoyalAgentsSection = memo(function RouteRoyalAgentsSection({
  agents,
  listings,
  title,
  subtitle,
  columns = 2,
  messages,
}: RouteRoyalAgentsSectionProps) {
  const resolvedAgents = useMemo(() => {
    if (agents && agents.length > 0) return agents;
    if (listings && listings.length > 0) {
      const derived = extractAgentsFromListings(listings);
      if (derived.length > 0) return derived;
    }
    return [];
  }, [agents, listings]);

  const tenant = useTenantOptional();
  const organisationId = tenant?.organisationId || '';

  const agentsMessages = messages?.agents as Record<string, unknown> | undefined;
  const displayTitle = title || safeGetString(agentsMessages?.title, 'Our Team');
  const displaySubtitle = subtitle || safeGetString(agentsMessages?.subtitle, 'Dedicated professionals ready to assist you');

  const cardMessages = {
    contact: safeGetString(agentsMessages?.contact, 'Contact'),
    call: safeGetString(agentsMessages?.call, 'Call'),
    email: safeGetString(agentsMessages?.email, 'Email'),
    scheduleMeeting: safeGetString(agentsMessages?.scheduleMeeting, 'Schedule Meeting'),
    properties: safeGetString(agentsMessages?.properties, 'Properties'),
    backToProfile: safeGetString(agentsMessages?.backToProfile, 'Back to Profile'),
  };

  if (resolvedAgents.length === 0) return null;

  return (
    <section className="py-20 lg:py-28 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {(displayTitle || displaySubtitle) && (
          <div className="text-center mb-14">
            {displayTitle && (
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3 tracking-tight">
                {displayTitle}
              </h2>
            )}
            {displaySubtitle && (
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">{displaySubtitle}</p>
            )}
          </div>
        )}
        <CoreAgentsSection
          agents={resolvedAgents}
          columns={columns}
          gap="xl"
          renderAgent={(agent, index) => (
            <FlipAgentCard
              agent={agent}
              index={index}
              organisationId={organisationId}
              messages={cardMessages}
            />
          )}
        />
      </div>
    </section>
  );
});
